import type { ReactElement } from 'react';
import type {
  MCPEnforcementSummary,
  MCPEnforcementView,
} from '@kirin/api-client/contracts/mcp';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface McpEnforcementFilterChipsProps {
  view: MCPEnforcementView | null;
  summary: MCPEnforcementSummary | undefined;
  onChange: (next: MCPEnforcementView | null) => void;
}

/**
 * KIR-4482 A5: "All servers | New (n) | Blocked (n)" chips above the MCP
 * Servers table. Counts are org-wide (from the list response meta). Styled
 * like the gateway's source-filter buttons (`MCPSourceFilterButtons`).
 */
export function McpEnforcementFilterChips({
  view,
  summary,
  onChange,
}: McpEnforcementFilterChipsProps): ReactElement {
  const options: {
    value: MCPEnforcementView | null;
    label: string;
    count?: number;
  }[] = [
    { value: null, label: 'All servers' },
    { value: 'new', label: 'New', count: summary?.newCount },
    { value: 'blocked', label: 'Blocked', count: summary?.blockedCount },
  ];

  return (
    <div
      role="group"
      aria-label="Filter MCP servers by enforcement"
      className="flex items-center gap-2"
    >
      {options.map(option => {
        const selected = option.value === view;
        return (
          <Button
            key={option.label}
            type="button"
            size="sm"
            variant="outline"
            aria-pressed={selected}
            className={cn(
              'gap-1.5',
              selected &&
                'bg-blue-600 text-white border-blue-600 hover:bg-blue-600/90 hover:text-white'
            )}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs font-semibold',
                  selected
                    ? 'bg-white/20 text-white'
                    : 'bg-muted text-foreground'
                )}
              >
                {option.count}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
